/**
 * Event Log Service
 * 
 * Handles event log-related API calls for visualization only
 */

import { api, handleApiError } from './api';
import type {
  EventLogResponse,
  EventLogFilters,
  EventLogListItem,
  EventLogStatistics,
} from '@/types';

/**
 * Event Log Service Object
 * Contains event log visualization API operations
 */
export const eventLogService = {
  /**
   * Get All Event Logs
   * Fetches event logs filtered by specific criteria
   * 
   * @param filters - Optional filters for event logs
   * @param filters.agentEventId - Filters logs by specific event ID (not agent ID)
   * @returns Promise with event logs list
   */
  getEventLogs: async (filters?: EventLogFilters): Promise<EventLogListItem[]> => {
    try {
      const queryParams = new URLSearchParams();
      
      if (filters?.agentEventId) {
        queryParams.append('agentEventId', filters.agentEventId.toString());
      }
      if (filters?.status) {
        queryParams.append('status', filters.status);
      }
      if (filters?.hasError !== undefined) {
        queryParams.append('hasError', filters.hasError.toString());
      }
      if (filters?.dateFrom) {
        queryParams.append('dateFrom', filters.dateFrom);
      }
      if (filters?.dateTo) {
        queryParams.append('dateTo', filters.dateTo);
      }

      const url = queryParams.toString() 
        ? `/event-logs?${queryParams.toString()}`
        : '/event-logs';

      const response = await api.get<EventLogResponse[]>(url);

      // Process data to add computed fields
      const processedData: EventLogListItem[] = response.data.map(log => ({
        ...log,
        hasError: !!log.errorMessage,
        isRecent: new Date(log.createdAt) > new Date(Date.now() - 24 * 60 * 60 * 1000), // Last 24 hours
      }));

      return processedData;
    } catch (error) {
      const apiError = handleApiError(error as any);
      throw new Error(apiError.message);
    }
  },

  /**
   * Get Event Log by Agent Event ID
   * Fetches a specific event log by agent event ID
   * 
   * @param agentEventId - Agent event ID
   * @returns Promise with event log details
   */
  getEventLogByAgentEventId: async (agentEventId: number): Promise<EventLogResponse> => {
    try {
      const response = await api.get<EventLogResponse>(`/event-logs/${agentEventId}`);
      return response.data;
    } catch (error) {
      const apiError = handleApiError(error as any);
      throw new Error(apiError.message);
    }
  },

  /**
   * Get Event Logs Statistics
   * Get statistics about event logs (success rate, error count, etc.)
   * 
   * @returns Promise with event log statistics
   */
  getEventLogStatistics: async (): Promise<EventLogStatistics> => {
    try {
      const logs = await eventLogService.getEventLogs();
      
      const total = logs.length;
      const successCount = logs.filter(log => log.status === 'SUCCESS').length;
      const errorCount = logs.filter(log => log.hasError).length;
      const successRate = total > 0 ? (successCount / total) * 100 : 0;
      const recentLogs = logs.filter(log => log.isRecent).length;

      // Calculate status distribution
      const statusDistribution: Record<string, number> = {};
      logs.forEach(log => {
        statusDistribution[log.status] = (statusDistribution[log.status] || 0) + 1;
      });

      return {
        total,
        successCount,
        errorCount,
        successRate,
        recentLogs,
        statusDistribution,
      };
    } catch (error) {
      const apiError = handleApiError(error as any);
      throw new Error(apiError.message);
    }
  },
};

export default eventLogService;